import React, { useState, useEffect } from 'react';
import Icon from '../shared/Icon';
import Chip from '../shared/Chip';
import DocumentFieldSettled from './DocumentFieldSettled';
import './DocumentFieldBlock.css';

/** `expiry_date` -> `Expiry Date`; `inspector_id` -> `Inspector ID`. */
export const humanizeFieldId = (fieldId) =>
  String(fieldId || '')
    .split('_')
    .filter(Boolean)
    .map(w => (w.toLowerCase() === 'id' ? 'ID' : w.charAt(0).toUpperCase() + w.slice(1)))
    .join(' ');

/**
 * One flagged field: what extraction returned, an input to confirm or correct
 * it, and a way to open the source document.
 *
 * Decisions are staged locally, not sent — the whole document is committed in a
 * single call once every field here is settled. "Can't verify" maps to `skip`,
 * which leaves the value unverified; the confirm-vs-correct classification is
 * the model's, not this component's.
 *
 * Everything keys off `flag.entity_id`. `field_id` is a display label only and
 * CAN repeat within one document, so it must never be used as an identity.
 */
const DocumentFieldBlock = ({
  flag,
  documentId,
  staged,
  isInvalid,
  onStage,
  onStageValue,
  onUnstage,
  onOpenSource,
}) => {
  const extracted = flag.extracted_value ?? '';
  const [value, setValue] = useState(extracted);
  const entityId = flag.entity_id;

  // Re-sync when the server hands back a fresh flag set (e.g. after a partial
  // commit) so the input never shows a value the server no longer holds.
  useEffect(() => {
    setValue(flag.extracted_value ?? '');
  }, [flag.entity_id, flag.extracted_value]);

  // row_label carries the source field name, so it is what tells apart two flags
  // sharing a field_id: "CT Head Date/Time" vs "Service Date/Time".
  const typeLabel = humanizeFieldId(flag.field_id);
  const label = flag.row_label ? `${typeLabel} — ${flag.row_label}` : typeLabel;
  const page = flag.evidence?.page ?? null;
  const trimmed = value.trim();

  // Classifying confirm-vs-correct is the model's job — it owns extracted_value.
  const handleSave = () => onStageValue(documentId, entityId, value);

  if (staged) {
    return (
      <DocumentFieldSettled
        label={label}
        staged={staged}
        extracted={extracted}
        onUndo={() => onUnstage(documentId, entityId)}
      />
    );
  }

  return (
    <div className={`resui-card resui-card--elevated docreview-field${isInvalid ? ' docreview-field--invalid' : ''}`}>
      {/* Figma 1018-2835: the chip lives in the card's HEADER row, right-aligned
          beside the field name — not inline beside the value. Every settled
          state does the same, so the chip stays in one place as a field moves
          through unverified → verified/corrected. */}
      <div className="docreview-field-head">
        <h4 className="docreview-field-title">{label}</h4>
        {!(extracted === '' || flag.extracted_value === null) && (
          <Chip variant="alert" className="docreview-chip">Unverified</Chip>
        )}
      </div>

      <div className="docreview-returned">
        {extracted === '' || flag.extracted_value === null ? (
          <span className="docreview-returned-nil">nothing returned</span>
        ) : (
          <span className="docreview-returned-value">{extracted}</span>
        )}
        {/* A plain link in the frame, not an outlined button with an icon. */}
        <button
          type="button"
          className="resui-link docreview-source-link"
          title={page ? `Opens at page ${page}` : 'Opens the full document'}
          onClick={() => onOpenSource(page)}
        >
          View document
        </button>
      </div>

      {/* The canonical ResUI Input, label and all — Figma 1018-2835 places the
          `Input` component here (node 1019:3636, 66px = 22 label + 4 gap + 40
          input), so `.resui-field` reproduces it exactly. It previously used a
          hand-rolled copy borrowed from CSV review, which rendered the label a
          size down and in grey. */}
      <div className="resui-field">
        {/* REQUIRED, not optional — confirmed by design against Figma 1018-2835,
            which now draws the red asterisk rather than "(optional)" in grey.
            This is a behaviour decision as much as a label: every field must be
            confirmed, corrected or marked "Can't verify" before the document is
            released, which is what the footer's `complete` gate enforces. Do not
            soften this to optional without reopening it with design — it would
            undo the settle-everything-then-release model. */}
        <label className="resui-field-label" htmlFor={`docreview-input-${entityId}`}>
          <span>{extracted === '' ? 'Enter the value' : 'Confirm or correct'}</span>
          <span className="resui-field-required">*</span>
        </label>
        <input
          id={`docreview-input-${entityId}`}
          type="text"
          className={`resui-input${isInvalid ? ' resui-input--error' : ''}`}
          value={value}
          autoComplete="off"
          spellCheck="false"
          placeholder={extracted === '' ? 'type the value from the document' : ''}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
        />
      </div>

      {isInvalid && (
        <p className="docreview-invalid-note">
          <Icon name="warning" />
          Confirm, correct, or mark this field to save the document
        </p>
      )}

      <div className="docreview-field-actions">
        <button
          type="button"
          className="resui-btn resui-btn--small resui-btn--primary"
          disabled={!trimmed}
          onClick={handleSave}
        >
          Save
        </button>
        {/* Sentence case, matching the settled chip. The capital V here was
            reproducing a slip in an older cut of Figma 1018-2835; design has
            since confirmed sentence case for every chip and button on the
            platform, with named areas ("Data Sources") the only exception. */}
        <button
          type="button"
          className="resui-btn resui-btn--small resui-btn--secondary"
          onClick={() => onStage(documentId, entityId, 'skip')}
        >
          Can’t verify
        </button>
      </div>
    </div>
  );
};

export default DocumentFieldBlock;
